import { motion } from "framer-motion";
import {
  Wrench, Hammer, Tv, Droplets, Frame, DoorOpen, Sofa, Sparkles, Car, Zap, ArrowUpRight,
} from "lucide-react";
import { useLanguage, waLink } from "../i18n";

const ICONS = [Wrench, Hammer, Tv, Droplets, Frame, DoorOpen, Sofa, Sparkles, Car, Zap];
const EASE = [0.22, 1, 0.36, 1];

const Services = () => {
  const { t } = useLanguage();
  return (
    <section id="servicios" className="py-24 sm:py-32" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-14 grid grid-cols-1 gap-6 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-8">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, ease: EASE }}
              className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-[#C85A32]"
            >
              {t.services.eyebrow}
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.08, ease: EASE }}
              className="font-serif text-4xl font-black tracking-tight text-[#0D1B2A] sm:text-5xl lg:text-6xl"
            >
              {t.services.title}
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.16, ease: EASE }}
            className="max-w-md text-base leading-relaxed text-[#0D1B2A]/60 lg:col-span-4"
          >
            {t.services.sub}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="services-grid">
          {t.services.items.map((s, i) => {
            const Icon = ICONS[i];
            return (
              <motion.article
                key={s.title}
                data-testid={`service-card-${i + 1}`}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.09, ease: EASE }}
                className="group relative flex flex-col rounded-3xl border border-[#E6DEC8] bg-white p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-[#C85A32]/50 hover:shadow-2xl hover:shadow-[#C85A32]/10"
              >
                <div className="mb-5 flex items-start justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F2E8D5] text-[#C85A32] transition-all duration-300 group-hover:bg-[#C85A32] group-hover:text-white">
                    <Icon size={22} strokeWidth={2} />
                  </span>
                  <span className="font-serif text-sm italic text-[#0D1B2A]/30">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-serif text-xl font-bold leading-snug text-[#0D1B2A] sm:text-2xl">
                  {s.title}
                </h3>
                <p className="mt-2.5 flex-1 text-sm leading-relaxed text-[#0D1B2A]/60">
                  {s.desc}
                </p>
                <a
                  data-testid={`service-quote-${i + 1}`}
                  href={waLink(s.wa)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-[#1B6B93] transition-colors duration-300 hover:text-[#C85A32]"
                >
                  {t.services.cta}
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
